import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MessageCircle, Clock, CheckCircle2, CheckCheck, XCircle, CircleDot, PackageCheck } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { DiaryModal } from "@/components/DiaryModal";
import { UserIdentity } from "@/components/UserIdentity";
import { useLanguage } from "@/context/LanguageContext";
import { RouteLine, AcceptsIcons } from "@/components/Cards";
import { ReviewModal } from "@/components/ReviewModal";
import { EditListingModal } from "@/components/EditListingModal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const STATUS_META = {
  pending: { icon: Clock, cls: "bg-amber-100 text-amber-800" },
  confirmed: { icon: CheckCircle2, cls: "bg-blue-100 text-blue-800" },
  completed: { icon: CheckCheck, cls: "bg-emerald-100 text-emerald-800" },
  cancelled: { icon: XCircle, cls: "bg-slate-100 text-slate-500" },
  active: { icon: CircleDot, cls: "bg-blue-100 text-blue-800" },
  delivered: { icon: PackageCheck, cls: "bg-emerald-100 text-emerald-800" },
};

const StatusBadge = ({ status }) => {
  const { t } = useLanguage();
  const meta = STATUS_META[status] || { icon: CircleDot, cls: "bg-slate-100 text-slate-600" };
  const Icon = meta.icon;
  return (
    <Badge data-testid={`status-${status}`} className={`${meta.cls} hover:${meta.cls} inline-flex items-center gap-1.5`}>
      <Icon size={13} />
      {t(`dashboard.status.${status}`)}
    </Badge>
  );
};

const StatusSteps = ({ status, inProgress }) => {
  const { t } = useLanguage();
  const steps = ["pending", "confirmed", "completed"];
  const current = steps.indexOf(status);
  return (
    <div className="flex items-center mt-2" data-testid="status-steps">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center">
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${i <= current ? "bg-emerald-500" : "bg-slate-200"}`} />
          <span className={`ml-1.5 text-[11px] whitespace-nowrap ${i <= current ? "text-emerald-700 font-semibold" : "text-slate-400"}`}>{t(`dashboard.steps.${s}`)}</span>
          {i < steps.length - 1 && <span className={`w-6 h-px mx-2 ${i < current ? "bg-emerald-400" : "bg-slate-200"}`} />}
        </div>
      ))}
      {inProgress && status === "confirmed" && (
        <span data-testid="status-in-progress" className="ml-3 inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
          {t("dashboard.steps.inProgress")}
        </span>
      )}
    </div>
  );
};

export default function Dashboard() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [parcels, setParcels] = useState([]);
  const [bookings, setBookings] = useState({ made: [], received: [] });
  const [reviewBooking, setReviewBooking] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const todayStr = new Date().toLocaleDateString("sv-SE");
  const [editing, setEditing] = useState(null);
  const [adminVerifs, setAdminVerifs] = useState([]);
  const [adminTickets, setAdminTickets] = useState([]);

  const setTicketStatus = async (id, status) => {
    try {
      await api.post(`/admin/support/tickets/${id}/status`, { status });
      setAdminTickets((prev) => prev.map((tk) => (tk.id === id ? { ...tk, status } : tk)));
    } catch (e) {}
  };
  const newTickets = adminTickets.filter((tk) => tk.status !== "resolved").length;
  const [cleanupPlan, setCleanupPlan] = useState(null);
  const [cleanupDone, setCleanupDone] = useState(null);
  const [cleanupBusy, setCleanupBusy] = useState(false);

  const analyzeCleanup = async () => {
    setCleanupBusy(true);
    try {
      const r = await api.get("/admin/cleanup/test-data");
      setCleanupPlan(r.data);
      setCleanupDone(null);
    } catch (e) {}
    setCleanupBusy(false);
  };

  const executeCleanup = async () => {
    setCleanupBusy(true);
    try {
      const r = await api.post("/admin/cleanup/test-data");
      setCleanupDone(r.data.deleted);
      setCleanupPlan(null);
    } catch (e) {}
    setCleanupBusy(false);
  };
  const [diary, setDiary] = useState(null);

  const load = useCallback(() => {
    api.get("/my/trips").then((r) => setTrips(r.data)).catch(() => {});
    api.get("/my/parcels").then((r) => setParcels(r.data)).catch(() => {});
    api.get("/my/bookings").then((r) => setBookings(r.data)).catch(() => {});
    if (user?.role === "admin") api.get("/admin/verifications").then((r) => setAdminVerifs(r.data)).catch(() => {});
    if (user?.role === "admin") api.get("/admin/support/tickets").then((r) => setAdminTickets(r.data)).catch(() => {});
  }, [user]);

  useEffect(load, [load]);

  const setStatus = async (bookingId, status) => {
    try {
      await api.post(`/bookings/${bookingId}/status`, { status });
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e, t));
    }
  };

  const cancelParcel = async (parcelId) => {
    try {
      await api.post(`/parcels/${parcelId}/cancel`);
      toast.success(t("dashboard.parcelCancelled"));
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e, t));
    }
  };

  const cancelTrip = async (tripId) => {
    try {
      await api.post(`/trips/${tripId}/cancel`);
      toast.success(t("dashboard.tripCancelled"));
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e, t));
    }
  };

  const reviewVerification = async (id, action) => {
    try {
      await api.post(`/admin/verifications/${id}/${action}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e, t));
    }
  };

  const completeTrip = async (tripId) => {
    try {
      await api.post(`/trips/${tripId}/complete`);
      toast.success(t("trip.completed"));
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const contact = async (userId, tripId) => {
    try {
      const { data } = await api.post("/conversations", { user_id: userId, trip_id: tripId });
      navigate(`/mensagens/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e, t));
    }
  };

  const BookingRow = ({ b, received }) => (
    <div className="bg-white border border-slate-100 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid={`booking-${b.id}`}>
      <div>
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <Badge variant="secondary">{b.type === "ride" ? t("dashboard.rideBooking") : t("dashboard.parcelBooking")}</Badge>
          <StatusBadge status={b.status} />
        </div>
        {b.status !== "cancelled" && <StatusSteps status={b.status} inProgress={b.trip?.date === todayStr} />}
        <p className="text-xs text-slate-500 mt-1.5" data-testid={`booking-help-${b.id}`}>{b.status === "completed" && b.reviewed_by_me ? t("dashboard.reviewSubmitted") : t(`dashboard.help.${received ? "received" : "made"}.${b.status}`)}</p>
        {b.trip && <RouteLine origin={b.trip.origin} stops={b.trip.stops} destination={b.trip.destination} compact />}
        <p className="text-xs text-slate-500 mt-1.5">
          {b.trip?.date} · {received ? (b.type === "ride" ? t("dashboard.passenger") : t("parcel.sender")) : t("trip.driver")}: <span className="inline-flex items-center gap-1.5 align-middle"><UserIdentity user={b.other_user} avatarSize="w-5 h-5" avatarText="text-[10px]" nameClass="text-xs font-semibold text-slate-700" testid={`booking-user-${b.id}`} /></span>
          {b.parcel && ` · ${b.parcel.origin} → ${b.parcel.destination}`}
        </p>
      </div>
      <div className="flex flex-wrap gap-2 shrink-0">
        {received && b.status === "pending" && (
          <>
            <Button size="sm" data-testid={`booking-confirm-${b.id}`} onClick={() => setConfirm({ kind: "confirmBooking", id: b.id })} className="rounded-full bg-primary text-white">{t("dashboard.confirm")}</Button>
            <Button size="sm" variant="outline" data-testid={`booking-cancel-${b.id}`} onClick={() => setConfirm({ kind: "booking", id: b.id })} className="rounded-full">{t("dashboard.cancel")}</Button>
          </>
        )}
        {received && b.status === "confirmed" && (
          <Button size="sm" data-testid={`booking-complete-${b.id}`} onClick={() => setConfirm({ kind: "completeBooking", id: b.id })} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">{t("dashboard.complete")}</Button>
        )}
        {!received && b.status === "pending" && (
          <Button size="sm" variant="outline" data-testid={`booking-cancel-${b.id}`} onClick={() => setConfirm({ kind: "booking", id: b.id })} className="rounded-full">{t("dashboard.cancel")}</Button>
        )}
        {b.status === "confirmed" && b.type === "ride" && b.trip?.date === todayStr && (
          <Button size="sm" variant="ghost" data-testid={`booking-diary-${b.id}`} onClick={() => setDiary({ b, canCheckin: !received })} className="rounded-full">{t("diary.view")}</Button>
        )}
        {b.status === "completed" && !b.reviewed_by_me && (
          <Button size="sm" data-testid={`booking-review-${b.id}`} onClick={() => setReviewBooking(b)} className="rounded-full bg-accent hover:bg-accent/90 text-white">{t("dashboard.review")}</Button>
        )}
        <Button size="sm" variant="ghost" data-testid={`booking-chat-${b.id}`} onClick={() => contact(b.other_user.id, b.trip_id)} className="rounded-full"><MessageCircle size={15} /></Button>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="dashboard-page">
      <h1 className="text-3xl md:text-4xl font-bold text-primary mb-8">{t("dashboard.title")}</h1>
      {user?.role === "admin" && newTickets > 0 && (
        <a href="#admin-support" data-testid="admin-support-banner" className="-mt-4 mb-8 flex items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-2xl p-4 hover:bg-amber-100 transition-colors">
          <span className="text-sm font-semibold text-amber-800">{t("support.adminTitle")}: {newTickets} {t("support.newCount")}</span>
          <span className="text-xs font-bold text-amber-700">{t("support.viewRequests")} →</span>
        </a>
      )}
      <Tabs defaultValue="bookings">
        <TabsList className="mb-8 rounded-full">
          <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-full">{t("dashboard.bookings")}</TabsTrigger>
          <TabsTrigger value="trips" data-testid="tab-trips" className="rounded-full">{t("dashboard.myTrips")}</TabsTrigger>
          <TabsTrigger value="parcels" data-testid="tab-parcels" className="rounded-full">{t("dashboard.myParcels")}</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="space-y-8">
          <div>
            <h2 className="text-lg font-bold text-primary mb-4">{t("dashboard.received")}</h2>
            <div className="space-y-3" data-testid="bookings-received">
              {bookings.received.length === 0 && <p className="text-sm text-slate-400">{t("dashboard.noBookings")}</p>}
              {bookings.received.map((b) => <BookingRow key={b.id} b={b} received />)}
            </div>
          </div>
          <div>
            <h2 className="text-lg font-bold text-primary mb-4">{t("dashboard.made")}</h2>
            <div className="space-y-3" data-testid="bookings-made">
              {bookings.made.length === 0 && <p className="text-sm text-slate-400">{t("dashboard.noBookings")}</p>}
              {bookings.made.map((b) => <BookingRow key={b.id} b={b} />)}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="trips" className="space-y-3" data-testid="my-trips-list">
          {trips.length === 0 && <p className="text-sm text-slate-400">{t("dashboard.noTrips")}</p>}
          {trips.map((trip) => (
            <div key={trip.id} className="bg-white border border-slate-100 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid={`my-trip-${trip.id}`}>
              <div>
                <RouteLine origin={trip.origin} stops={trip.stops} destination={trip.destination} />
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                  <span>{trip.date}{trip.time ? ` · ${trip.time}` : ""}</span>
                  <AcceptsIcons accepts={trip.accepts} />
                  <StatusBadge status={trip.status} />
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Link to={`/viagem/${trip.id}`}><Button size="sm" variant="outline" data-testid={`my-trip-view-${trip.id}`} className="rounded-full">{t("common.seeProfile") === "Ver perfil" ? "Ver" : "View"}</Button></Link>
                {trip.status === "active" && <Button size="sm" variant="outline" data-testid={`my-trip-edit-${trip.id}`} onClick={() => setEditing({ kind: "trip", item: trip })} className="rounded-full">{t("dashboard.edit")}</Button>}
                {trip.status === "active" && <Button size="sm" data-testid={`my-trip-complete-${trip.id}`} onClick={() => setConfirm({ kind: "trip", id: trip.id })} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">{t("dashboard.completeTrip")}</Button>}
                {trip.status === "active" && <Button size="sm" variant="outline" data-testid={`my-trip-cancel-${trip.id}`} onClick={() => setConfirm({ kind: "cancelTrip", id: trip.id })} className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/5">{t("dashboard.cancelTrip")}</Button>}
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="parcels" className="space-y-3" data-testid="my-parcels-list">
          {parcels.length === 0 && <p className="text-sm text-slate-400">{t("dashboard.noParcels")}</p>}
          {parcels.map((p) => (
            <div key={p.id} className="bg-white border border-slate-100 rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid={`my-parcel-${p.id}`}>
              <div>
                <p className="font-semibold text-primary">{p.origin} → {p.destination}</p>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                  <span>{t("parcel.deliverBy")} {p.date}</span>
                  <StatusBadge status={p.status} />
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Link to={`/encomenda/${p.id}`}>
                  <Button size="sm" variant="outline" data-testid={`my-parcel-matches-${p.id}`} className="rounded-full">{t("dashboard.viewMatches")}</Button>
                </Link>
                {p.status === "active" && (
                  <Button size="sm" variant="outline" data-testid={`my-parcel-edit-${p.id}`} onClick={() => setEditing({ kind: "parcel", item: p })} className="rounded-full">{t("dashboard.edit")}</Button>
                )}
                {p.status === "active" && (
                  <Button size="sm" variant="outline" data-testid={`my-parcel-cancel-${p.id}`} onClick={() => setConfirm({ kind: "parcel", id: p.id })} className="rounded-full text-destructive border-destructive/40 hover:bg-destructive/5">{t("dashboard.cancelParcel")}</Button>
                )}
              </div>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      {user?.role === "admin" && (
        <div className="mt-12" data-testid="admin-verifications">
          <h2 className="text-lg font-bold text-primary mb-4">{t("verify.adminTitle")}</h2>
          {adminVerifs.length === 0 && <p className="text-sm text-slate-400" data-testid="admin-verifications-empty">{t("verify.adminNone")}</p>}
          <div className="space-y-3">
            {adminVerifs.map((v) => (
              <div key={v.id} className="bg-white border border-slate-100 rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`admin-verif-${v.id}`}>
                <div>
                  <p className="font-semibold text-primary text-sm">{v.user?.name} <span className="text-slate-400 font-normal">({v.user?.email})</span></p>
                  <p className="text-xs text-slate-500 mt-1">{v.document_type === "cc" ? t("verify.cc") : v.document_type === "passport" ? t("verify.passport") : t("verify.driving")} · {v.document_number}</p>
                  {v.document_url && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${v.document_url}`} target="_blank" rel="noreferrer">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${v.document_url}`} alt="doc" data-testid={`admin-doc-${v.id}`} className="mt-2 h-20 rounded-lg border border-slate-200 object-cover" />
                    </a>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" data-testid={`admin-approve-${v.id}`} onClick={() => reviewVerification(v.id, "approve")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white">{t("verify.approve")}</Button>
                  <Button size="sm" variant="outline" data-testid={`admin-reject-${v.id}`} onClick={() => reviewVerification(v.id, "reject")} className="rounded-full">{t("verify.reject")}</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {user?.role === "admin" && (
        <div className="mt-12" id="admin-support" data-testid="admin-support">
          <h2 className="text-lg font-bold text-primary mb-4 flex items-center gap-2">{t("support.adminTitle")}{newTickets > 0 && <span data-testid="admin-support-badge" className="bg-amber-400 text-primary text-xs font-bold px-2.5 py-0.5 rounded-full">{newTickets}</span>}</h2>
          {adminTickets.length === 0 && <p className="text-sm text-slate-400" data-testid="admin-support-empty">{t("support.adminNone")}</p>}
          <div className="space-y-3">
            {adminTickets.map((tk) => (
              <div key={tk.id} className="bg-white border border-slate-100 rounded-2xl p-5 flex items-start justify-between gap-4 flex-wrap" data-testid={`admin-ticket-${tk.id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-primary text-sm">{t(`help.categories.${tk.category}`)}</p>
                    <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${tk.status === "resolved" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`} data-testid={`admin-ticket-status-${tk.id}`}>{tk.status === "resolved" ? t("support.statusResolved") : t("support.statusNew")}</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{tk.user_name} ({tk.user_email}) · {new Date(tk.created_at).toLocaleDateString()}</p>
                  <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{tk.description}</p>
                  {tk.attachment_url && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${tk.attachment_url}`} target="_blank" rel="noreferrer" className="text-xs text-accent font-semibold mt-2 inline-block" data-testid={`admin-ticket-attachment-${tk.id}`}>{t("support.attachmentLink")}</a>
                  )}
                </div>
                <Button size="sm" variant={tk.status === "resolved" ? "outline" : "default"} data-testid={`admin-ticket-toggle-${tk.id}`} onClick={() => setTicketStatus(tk.id, tk.status === "resolved" ? "new" : "resolved")} className={tk.status === "resolved" ? "rounded-full" : "rounded-full bg-emerald-600 hover:bg-emerald-700 text-white"}>
                  {tk.status === "resolved" ? t("support.markNew") : t("support.markResolved")}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {user?.role === "admin" && (
        <div className="mt-12" data-testid="admin-cleanup">
          <h2 className="text-lg font-bold text-primary mb-2">{t("cleanup.title")}</h2>
          <p className="text-sm text-slate-500 mb-4">{t("cleanup.intro")}</p>
          {!cleanupPlan && !cleanupDone && (
            <Button data-testid="admin-cleanup-analyze" onClick={analyzeCleanup} disabled={cleanupBusy} variant="outline" className="rounded-full">{cleanupBusy ? "…" : t("cleanup.analyze")}</Button>
          )}
          {cleanupPlan && (
            <div className="bg-white border border-slate-100 rounded-2xl p-5 space-y-2" data-testid="admin-cleanup-plan">
              {Object.entries(cleanupPlan.counts).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm"><span className="text-slate-600">{t(`cleanup.cols.${k}`)}</span><span className="font-bold text-primary">{v}</span></div>
              ))}
              {cleanupPlan.samples?.users?.length > 0 && <p className="text-xs text-slate-400 pt-2" data-testid="admin-cleanup-sample-users">{cleanupPlan.samples.users.join(" · ")}</p>}
              {cleanupPlan.samples?.trips?.length > 0 && <p className="text-xs text-slate-400" data-testid="admin-cleanup-sample-trips">{cleanupPlan.samples.trips.join(" · ")}</p>}
              {Object.values(cleanupPlan.counts).every((v) => v === 0) ? (
                <p className="text-sm text-emerald-600 font-semibold pt-2" data-testid="admin-cleanup-none">{t("cleanup.none")}</p>
              ) : (
                <Button data-testid="admin-cleanup-delete" onClick={executeCleanup} disabled={cleanupBusy} variant="destructive" className="rounded-full mt-2">{cleanupBusy ? "…" : t("cleanup.delete")}</Button>
              )}
            </div>
          )}
          {cleanupDone && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="admin-cleanup-done">
              <p className="text-sm font-semibold text-emerald-700 mb-2">{t("cleanup.done")}</p>
              {Object.entries(cleanupDone).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm text-emerald-800"><span>{t(`cleanup.cols.${k}`)}</span><span className="font-bold">{v}</span></div>
              ))}
            </div>
          )}
        </div>
      )}

      {diary && (
        <DiaryModal booking={diary.b} canCheckin={diary.canCheckin} open={!!diary} onClose={() => setDiary(null)} />
      )}

      {confirm && (
        <ConfirmDialog
          open={!!confirm}
          onClose={() => setConfirm(null)}
          onConfirm={() => (confirm.kind === "booking" ? setStatus(confirm.id, "cancelled") : confirm.kind === "completeBooking" ? setStatus(confirm.id, "completed") : confirm.kind === "confirmBooking" ? setStatus(confirm.id, "confirmed") : confirm.kind === "trip" ? completeTrip(confirm.id) : confirm.kind === "cancelTrip" ? cancelTrip(confirm.id) : cancelParcel(confirm.id))}
          title={confirm.kind === "booking" ? t("dashboard.confirmCancelBookingTitle") : confirm.kind === "completeBooking" ? t("dashboard.confirmCompleteBookingTitle") : confirm.kind === "confirmBooking" ? t("dashboard.confirmBookingTitle") : confirm.kind === "trip" ? t("dashboard.confirmCompleteTripTitle") : confirm.kind === "cancelTrip" ? t("dashboard.confirmCancelTripTitle") : t("dashboard.confirmCancelParcelTitle")}
          description={confirm.kind === "booking" ? t("dashboard.confirmCancelBookingText") : confirm.kind === "completeBooking" ? t("dashboard.confirmCompleteBookingText") : confirm.kind === "confirmBooking" ? t("dashboard.confirmBookingText") : confirm.kind === "trip" ? t("dashboard.confirmCompleteTripText") : confirm.kind === "cancelTrip" ? t("dashboard.confirmCancelTripText") : t("dashboard.confirmCancelParcelText")}
          confirmLabel={confirm.kind === "confirmBooking" ? t("dashboard.confirmBookingYes") : confirm.kind === "completeBooking" ? t("dashboard.confirmCompleteBookingYes") : confirm.kind === "trip" ? t("dashboard.confirmCompleteYes") : t("dashboard.confirmYes")}
          cancelLabel={(confirm.kind === "completeBooking" || confirm.kind === "confirmBooking") ? t("dashboard.cancel") : t("dashboard.confirmNo")}
          confirmClassName={confirm.kind === "confirmBooking" ? "bg-primary hover:bg-primary/90" : (confirm.kind === "trip" || confirm.kind === "completeBooking") ? "bg-emerald-600 hover:bg-emerald-700" : undefined}
        />
      )}

      {reviewBooking && (
        <ReviewModal booking={reviewBooking} open={!!reviewBooking} onClose={() => setReviewBooking(null)} onSubmitted={load} />
      )}

      {editing && (
        <EditListingModal kind={editing.kind} item={editing.item} open={!!editing} onClose={() => setEditing(null)} onSaved={load} />
      )}
    </div>
  );
}
